import {getPageSizes, getStatusText, getTableColumns, getTableRows} from '@common'
import {ContractStatus, FormFieldType} from '@enums'
import {Stack, Typography} from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterInitialState,
  GridToolbarColumnsButton,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid'

import React, {useMemo, useState} from 'react'

import {t} from 'i18next'

import {UIKit} from '@components'

import {
  SearchContainer,
  StyledButton,
  StyledToolbarWrapper,
  TableContainer,
  ToolbarInnerWrapper,
  TopBarContainer,
} from './style'
import {TableFilters} from './table-filters'

type RowType<T extends object> = T

type ButtonProps = {
  buttonText: string
  onClick: () => void
}

export interface FilteringProp {
  field: string
  type: FormFieldType.INPUT | FormFieldType.DROPDOWN | FormFieldType.MULTI_DROPDOWN
}

interface Props<T extends object> {
  columns: GridColDef[]
  rows: RowType<T>[]
  title: string
  filterConfig?: FilteringProp[]
  hidePagination?: boolean
  hideSearch?: boolean
  hideToolbar?: boolean
  buttonProps?: ButtonProps
  additionalButtonProps?: ButtonProps
  tableFilterModel?: GridFilterInitialState
  visibilityModel?: GridColumnVisibilityModel
}

/**
 * Table Component with optional title and button props
 */
export function Table<T extends object>(props: Props<T>) {
  const {
    columns,
    rows,
    hidePagination,
    additionalButtonProps,
    hideToolbar,
    hideSearch,
    title,
    visibilityModel,
    tableFilterModel,
    filterConfig,
    buttonProps,
  } = props

  const pageSizeOptions: UIKit.DropdownItem[] = useMemo(() => getPageSizes(), [])
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: Number(pageSizeOptions[0].value),
  })

  const displayRows = useMemo(() => getTableRows(rows), [rows])
  const displayColumns = useMemo(() => getTableColumns(columns), [columns])

  function CustomToolbar() {
    return (
      <StyledToolbarWrapper>
        <ToolbarInnerWrapper>
          <GridToolbarColumnsButton />
          {!filterConfig && <GridToolbarFilterButton />}
          <GridToolbarExport printOptions={{disableToolbarButton: true}} />
        </ToolbarInnerWrapper>
        {!hideSearch && !filterConfig && <GridToolbarQuickFilter />}
      </StyledToolbarWrapper>
    )
  }

  const [filteringObject, setFilteringObject] = useState(
    Object.fromEntries((filterConfig || [])?.map(item => [item.field, item.type === FormFieldType.INPUT ? '' : []])),
  )

  const generatedFilteringFieldsConfig = useMemo(() => {
    return columns
      .filter(column => filterConfig?.find(config => config.field === column.field))
      ?.map(item => {
        const fieldType = filterConfig?.find(config => config.field === item.field)?.type as FormFieldType
        return {
          fieldType: filterConfig?.find(config => config.field === item.field)?.type as FormFieldType,
          name: item.field,
          onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFilteringObject(prev => ({...prev, [item.field]: event.target.value})),
          value: filteringObject[item.field],
          label: item.headerName,
          ...(fieldType === FormFieldType.INPUT
            ? {}
            : {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                items: Array.from(new Set(rows.map(row => (row as any)[item.field]))).map(option => ({
                  value: option,
                  label: item.field === 'status' ? getStatusText(t, option as ContractStatus) : option,
                })),
              }),
        }
      })
  }, [columns, filterConfig, filteringObject])

  const filteredRows = useMemo(() => {
    return displayRows.filter(row => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const currentRow = row as any
      return Object.entries(filteringObject).every(([key, val]) => {
        if (val === '' || (Array.isArray(val) && val.length === 0)) return true

        if (Array.isArray(val)) {
          return (val as string[]).includes(String(currentRow[key]))
        } else {
          return String(currentRow[key]).toLowerCase().includes(val.toLowerCase())
        }
      })
    })
  }, [displayRows, filteringObject])

  const onClearFilter = () => {
    setFilteringObject(
      Object.fromEntries((filterConfig || [])?.map(item => [item.field, item.type === FormFieldType.INPUT ? '' : []])),
    )
  }

  const onApplyFilter = (newObject: {[k: string]: string | string[]}) =>
    setFilteringObject(newObject as {[k: string]: string | never[]})

  return (
    <TableContainer>
      <TopBarContainer>
        <Typography variant="h2">{title}</Typography>
        <SearchContainer>
          {buttonProps && (
            <StyledButton onClick={buttonProps.onClick} variant="contained">
              {buttonProps.buttonText}
            </StyledButton>
          )}
          {additionalButtonProps && (
            <StyledButton onClick={additionalButtonProps.onClick} variant="outlined">
              {additionalButtonProps.buttonText}
            </StyledButton>
          )}
        </SearchContainer>
      </TopBarContainer>
      {filterConfig && filterConfig.length > 0 && (
        <TableFilters
          formFields={generatedFilteringFieldsConfig}
          onApplyFilter={onApplyFilter}
          onClearFilter={onClearFilter}
        />
      )}
      <Stack style={{height: paginationModel.pageSize * 40 + 200}}>
        <DataGrid
          disableRowSelectionOnClick
          getRowHeight={() => 40}
          rows={filteredRows}
          initialState={{
            columns: {
              columnVisibilityModel: visibilityModel,
            },
            filter: tableFilterModel,
          }}
          columns={displayColumns}
          getRowId={row => `${title}-${row.no}`}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={model => setPaginationModel(model)}
          pageSizeOptions={pageSizeOptions.map(option => Number(option.value))}
          slotProps={{
            toolbar: {
              showQuickFilter: !hideSearch,
            },
          }}
          slots={!hideToolbar ? {toolbar: CustomToolbar} : undefined}
          hideFooter={hidePagination}
        />
      </Stack>
    </TableContainer>
  )
}
